import { ErrorResult } from "@/components/ui/ErrorResult";
import { LoadingSection } from "@/components/ui/loadings/LoadingSection";
import { useIngredients } from "@/services/ingredients/queries";
import { useCreateProductRecipe, useUpdateProductRecipe } from "@/services/product/mutation";
import { useProductRecipe } from "@/services/product/queries";
import { RecipeIngredientItem } from "@/types/recipe";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Descriptions, Modal, Table, Form, InputNumber, Select, Space, Tabs, message } from "antd";
import { ColumnsType } from "antd/es/table";
import { forwardRef, useImperativeHandle, useState, useEffect } from "react";

export interface ModalVRecipeRef {
  childFunction: (product: { id: string; name: string }) => void;
}

export interface ModalVRecipeProps {
  isReadOnly?: boolean;
}

export const ModalVRecipe = forwardRef<ModalVRecipeRef, ModalVRecipeProps>((props, ref) => {
  const { isReadOnly = false } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [product, setProduct] = useState<{ id: string; name: string } | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"view" | "edit">("view");

  const [form] = Form.useForm();
  const watchedIngredients = Form.useWatch("ingredients", form);

  const [messageApi, contextHolder] = message.useMessage();

  const { data: recipe, isLoading: isLoadingRecipe, isFetching: isFetchingRecipe, isError: isErrorRecipe, error: errorRecipe, refetch } =
    useProductRecipe(product?.id || "", { enabled: open && !!product?.id });

  // Fetch all ingredients for the selection dropdown
  const { data: ingredientsData, isLoading: isLoadingIngredients } = useIngredients(
    { pagination: { current: 1, pageSize: 1000 } },
    undefined,
    { enabled: open }
  );

  const createRecipeMutation = useCreateProductRecipe();
  const updateRecipeMutation = useUpdateProductRecipe();

  const childFunction = (selectedProduct: { id: string; name: string }) => {
    setProduct(selectedProduct);
    setIsCreating(false);
    setActiveTab("view");
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    childFunction,
  }));

  // Populate form fields when recipe changes
  useEffect(() => {
    if (recipe) {
      form.setFieldsValue({
        margin: recipe.margin,
        ingredients: recipe.ingredients.map((i) => ({
          ingredient_id: i.ingredientId,
          quantity: i.quantity,
        })),
      });
    } else {
      form.resetFields();
    }
  }, [recipe, form]);

  const handleStartCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      margin: null,
      ingredients: [{ ingredient_id: undefined, quantity: undefined }],
    });
    setIsCreating(true);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    form.resetFields();
  };

  const onFinish = (values: any) => {
    if (!product) return;

    if (!values.ingredients || values.ingredients.length === 0) {
      messageApi.error("Debes agregar al menos un ingrediente.");
      return;
    }

    const ids = values.ingredients.map((i: any) => i?.ingredient_id).filter(Boolean);
    const hasDuplicates = new Set(ids).size !== ids.length;
    if (hasDuplicates) {
      messageApi.error("No se puede agregar el mismo ingrediente más de una vez.");
      return;
    }

    const payload = {
      margin: values.margin !== undefined ? values.margin : null,
      ingredients: values.ingredients.map((i: any) => ({
        ingredient_id: i.ingredient_id,
        quantity: Number(i.quantity),
      })),
    };

    if (recipe) {
      // Edit mode
      updateRecipeMutation.mutate(
        {
          productId: product.id,
          payload: {
            version: recipe.version,
            ...payload,
          },
        },
        {
          onSuccess: () => {
            messageApi.success("Receta actualizada con éxito.");
            setActiveTab("view");
          },
          onError: (err: any) => {
            messageApi.error(
              err?.response?.data?.message || "Error al actualizar la receta."
            );
          },
        }
      );
    } else {
      // Create mode
      createRecipeMutation.mutate(
        {
          productId: product.id,
          payload,
        },
        {
          onSuccess: () => {
            messageApi.success("Receta creada con éxito.");
            setIsCreating(false);
            setActiveTab("view");
          },
          onError: (err: any) => {
            messageApi.error(
              err?.response?.data?.message || "Error al crear la receta."
            );
          },
        }
      );
    }
  };

  const columns: ColumnsType<RecipeIngredientItem> = [
    {
      title: "Ingrediente",
      dataIndex: "name",
      key: "name",
      className: "font-semibold text-foreground",
    },
    {
      title: "Cantidad",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (value: number) => (
        <span className="font-mono text-primary font-bold">
          {Number(value).toLocaleString("es-CL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
          })}
        </span>
      ),
    },
    {
      title: "Unidad",
      dataIndex: "unit",
      key: "unit",
      width: 150,
      render: (value: string) => {
        const unitLabels: Record<string, string> = {
          g: "Gramos (g)",
          kg: "Kilogramos (kg)",
          ml: "Mililitros (ml)",
          l: "Litros (L)",
          unit: "Unidad",
          bottle: "Botella",
          can: "Lata",
        };
        return unitLabels[value] || value;
      },
    },
  ];

  const ingredientOptions = (ingredientsData?.items || [])
    .filter((i) => i.available !== false)
    .map((i) => ({
      label: i.name,
      value: i.id,
    }));

  const getUnitForIngredient = (id?: string) => {
    if (!id) return "";
    const ing = ingredientsData?.items?.find((item) => item.id === id);
    if (!ing) return "";
    const unitLabels: Record<string, string> = {
      g: "g",
      kg: "kg",
      ml: "ml",
      l: "L",
      unit: "Unidades",
      bottle: "Botellas",
      can: "Latas",
    };
    return unitLabels[ing.unit] || ing.unit;
  };

  const isSubmitting = createRecipeMutation.isPending || updateRecipeMutation.isPending;

  const renderForm = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ margin: null, ingredients: [] }}
    >
      <Form.Item
        name="margin"
        label="Margen permitido (%)"
        rules={[{ type: "number", min: 0, max: 100, message: "Ingresa un porcentaje válido (0-100)" }]}
      >
        <InputNumber
          min={0}
          max={100}
          placeholder="Ej. 10"
          style={{ width: "100%" }}
          formatter={(value) => (value !== undefined ? `${value}%` : "")}
          parser={(value) => (value ? parseFloat(value.replace("%", "")) : 0) as any}
        />
      </Form.Item>

      <div className="mb-4">
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Ingredientes de la Receta
        </h4>
        <Form.List name="ingredients">
          {(fields, { add, remove }) => (
            <div className="space-y-3">
              {fields.map(({ key, name, ...restField }) => {
                const ingredientId = watchedIngredients?.[name]?.ingredient_id;
                const unitLabel = getUnitForIngredient(ingredientId);

                return (
                  <div
                    key={key}
                    className="flex gap-4 items-center bg-card/20 p-3 rounded-lg border border-border/40"
                  >
                    <div className="flex-1">
                      <Form.Item
                        {...restField}
                        name={[name, "ingredient_id"]}
                        rules={[{ required: true, message: "Ingrediente req." }]}
                        noStyle
                      >
                        <Select
                          showSearch
                          placeholder="Selecciona ingrediente"
                          optionFilterProp="label"
                          options={ingredientOptions}
                          style={{ width: "100%" }}
                          loading={isLoadingIngredients}
                        />
                      </Form.Item>
                    </div>

                    <div className="w-32 flex items-center gap-2">
                      <Form.Item
                        {...restField}
                        name={[name, "quantity"]}
                        rules={[{ required: true, message: "Cant. req." }]}
                        noStyle
                      >
                        <InputNumber
                          min={0.001}
                          placeholder="Cant."
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                      {unitLabel && (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {unitLabel}
                        </span>
                      )}
                    </div>

                    <MinusCircleOutlined
                      className="text-red-500 hover:text-red-600 text-lg cursor-pointer shrink-0"
                      onClick={() => remove(name)}
                    />
                  </div>
                );
              })}

              <Button
                type="dashed"
                onClick={() => add({ ingredient_id: undefined, quantity: undefined })}
                block
                icon={<PlusOutlined />}
              >
                Agregar ingrediente
              </Button>
            </div>
          )}
        </Form.List>
      </div>

      <Form.Item className="flex justify-end mt-6 pt-4 border-t border-border/50">
        <Space>
          {recipe ? null : (
            <Button onClick={handleCancelCreate} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
          >
            Guardar Receta
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );

  return (
    <Modal
      title={product ? `Receta: ${product.name}` : "Receta de producto"}
      centered
      open={open}
      width={700}
      onCancel={() => setOpen(false)}
      footer={
        isCreating || activeTab === "edit" ? null : (
          <Space>
            <Button onClick={() => setOpen(false)}>Cerrar</Button>
            {!recipe && !isReadOnly && (
              <Button type="primary" onClick={handleStartCreate}>
                Crear Receta
              </Button>
            )}
          </Space>
        )
      }
    >
      {contextHolder}

      {isLoadingRecipe ? (
        <div className="flex justify-center items-center py-10">
          <LoadingSection />
        </div>
      ) : isErrorRecipe ? (
        <div className="py-5">
          <ErrorResult error={errorRecipe} refetch={refetch} />
        </div>
      ) : (
        <div className="mt-4">
          {recipe ? (
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as any)}
              items={[
                {
                  key: "view",
                  label: "Ver Receta",
                  children: (
                    <div className="space-y-5 pt-3">
                      {recipe.margin !== null && (
                        <Descriptions
                          bordered
                          column={1}
                          size="small"
                          layout="horizontal"
                          className="mb-4"
                        >
                          <Descriptions.Item label="Margen permitido">
                            <span className="font-mono font-semibold">
                              {recipe.margin}%
                            </span>
                          </Descriptions.Item>
                        </Descriptions>
                      )}

                      <h4 className="text-sm font-semibold text-foreground mt-6 mb-2">
                        Ingredientes requeridos
                      </h4>
                      <Table<RecipeIngredientItem>
                        columns={columns}
                        dataSource={recipe.ingredients}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                        scroll={{ y: 300 }}
                      />
                    </div>
                  ),
                },
                ...(!isReadOnly
                  ? [
                      {
                        key: "edit",
                        label: "Editar Receta",
                        children: <div className="pt-3">{renderForm()}</div>,
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            <div className="space-y-4 py-2">
              {!isCreating ? (
                <Alert
                  message="Sin Receta"
                  description="Este producto no tiene una receta registrada en el sistema."
                  type="info"
                  showIcon
                />
              ) : (
                <div className="pt-2">{renderForm()}</div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
});
